import { Controller } from "@hotwired/stimulus"

// Real-time pre-submit status for the PO review form. Watches every
// input in the form, surfaces a banner above the Create Order button
// summarizing anything the operator should look at before submitting,
// and sets a title-attribute tooltip on the button itself.
//
// Issues we check for (none are hard blockers — the operator can still
// submit; this is just visibility so they aren't clicking into the dark):
//   * customer not selected / new-customer name blank
//   * any non-skipped row has $0 unit price
//   * any row marked OUT OF STOCK
//   * any row whose action is "create_new" (these will land as standalone
//     SKUs — the family-guard controller will also intercept)
//
// Targets:
//   banner        - the wrapper that shows/hides based on issue count
//   summary       - the <ul> we fill with issue lines
//   submitButton  - the Create Order button (title attribute target)
export default class extends Controller {
    static targets = ["banner", "summary", "submitButton"]

    connect() {
        this._refresh = this._refresh.bind(this)
        this._interceptSubmit = this._interceptSubmit.bind(this)
        this._refresh()
        // Live updates — when the operator fixes anything the banner
        // shrinks immediately so they know they've cleared an issue.
        this.element.addEventListener("input",  this._refresh)
        this.element.addEventListener("change", this._refresh)
        // Capture-phase submit interception so we run BEFORE Turbo's
        // turbo_confirm. The family-guard controller does the same;
        // both bypass cleanly with this._bypass.
        this.element.addEventListener("submit", this._interceptSubmit, { capture: true })
        this._bypass = false
    }

    disconnect() {
        this.element.removeEventListener("input",  this._refresh)
        this.element.removeEventListener("change", this._refresh)
        this.element.removeEventListener("submit", this._interceptSubmit, { capture: true })
    }

    // When the operator clicks Create Order without a customer, the
    // server rejects with a 422 — a dead bounce from the user's POV.
    // Intercept here, scroll to the customer section, and focus the
    // right input so the dead click becomes a guided action.
    _interceptSubmit(event) {
        if (this._bypass) {
            this._bypass = false
            return
        }

        const missing = this._missingCustomer()
        if (!missing) return

        event.preventDefault()
        event.stopPropagation()

        const anchor = document.getElementById("customer-section")
        if (anchor) {
            anchor.scrollIntoView({ behavior: "smooth", block: "start" })
            // Brief highlight pulse so the operator's eye lands on it.
            anchor.style.transition  = "box-shadow 0.3s ease"
            anchor.style.boxShadow   = "0 0 0 3px #fcd34d"
            setTimeout(() => { anchor.style.boxShadow = "" }, 1600)
        }

        // Focus the most useful input given the current mode.
        const focusTarget = missing === "existing"
            ? this.element.querySelector('select[name="customer_id"]')
            : this.element.querySelector('input[name="new_customer[name]"]')
        if (focusTarget) setTimeout(() => focusTarget.focus(), 350)
    }

    // Returns "existing" if the operator's in existing-customer mode
    // with nothing picked, "new" if in new-customer mode without a
    // name, or null if customer info is present.
    _missingCustomer() {
        const mode = this.element.querySelector('input[name="customer_mode"]:checked')
        if (!mode) return null
        if (mode.value === "existing") {
            const sel = this.element.querySelector('select[name="customer_id"]')
            return (!sel || !sel.value) ? "existing" : null
        }
        if (mode.value === "new") {
            const nameInput = this.element.querySelector('input[name="new_customer[name]"]')
            return (!nameInput || !nameInput.value.trim()) ? "new" : null
        }
        return null
    }

    _refresh() {
        const issues = this._collectIssues()
        this._renderBanner(issues)
        this._setSubmitHint(issues)
    }

    _collectIssues() {
        const issues = []

        // Customer first — it's the one TRUE hard-block (server rejects
        // without a customer). Surface it at the top of the banner so
        // operators see it before scanning the other reminders.
        const missing = this._missingCustomer()
        if (missing === "existing") issues.push("Pick a customer (or switch to 'Create new customer') — required")
        if (missing === "new")      issues.push("New customer needs a name — required")

        const rows = this.element.querySelectorAll('tr[data-controller~="po-row"]')

        let zeroPriceCount = 0
        let oosCount       = 0
        let createNewCount = 0

        rows.forEach((row) => {
            const productSelect = row.querySelector('select[name*="[product_id]"]')
            const actionSelect  = row.querySelector('select[name*="[action]"]')
            const priceInput    = row.querySelector('input[name*="[unit_price]"]')

            // Skip rows the operator explicitly marked "skip" (the row's
            // dimmed and won't be saved).
            const isSkipped = (!productSelect || productSelect.value === "") &&
                              actionSelect && actionSelect.value === "skip"
            if (isSkipped) return

            // $0 unit price — common after auto-extract from a handwritten
            // PO where the AI couldn't find numbers. Operator should fill
            // these in before submitting, or accept the $0.
            if (priceInput && parseFloat(priceInput.value || "0") === 0) {
                zeroPriceCount++
            }

            // OUT OF STOCK marker comes from the stock_status annotation.
            // We detect it from the rendered badge text since there's no
            // data-attribute to read.
            if (row.textContent.includes("Out of stock")) {
                oosCount++
            }

            // "Create new product" lines — the family-guard controller
            // also confirms these at submit, but mentioning them in the
            // banner gives operators a heads-up earlier.
            const willCreateNew = (!productSelect || productSelect.value === "") &&
                                  actionSelect && actionSelect.value === "create_new"
            if (willCreateNew) createNewCount++
        })

        if (zeroPriceCount > 0) {
            issues.push(`${zeroPriceCount} item${zeroPriceCount > 1 ? "s have" : " has"} no unit price — fill in or accept $0`)
        }
        if (oosCount > 0) {
            issues.push(`${oosCount} item${oosCount > 1 ? "s are" : " is"} out of stock — order can still be created but will need backorder handling`)
        }
        if (createNewCount > 0) {
            issues.push(`${createNewCount} item${createNewCount > 1 ? "s" : ""} will be created as new standalone product${createNewCount > 1 ? "s" : ""}`)
        }

        return issues
    }

    _renderBanner(issues) {
        if (!this.hasBannerTarget) return

        if (issues.length === 0) {
            this.bannerTarget.style.display = "none"
            if (this.hasSubmitButtonTarget) {
                this.submitButtonTarget.style.opacity = ""
            }
            return
        }

        this.bannerTarget.style.display = "flex"
        if (this.hasSummaryTarget) {
            this.summaryTarget.innerHTML = issues
                .map((i) => `<li style="margin: 0.125rem 0;">${this._escape(i)}</li>`)
                .join("")
        }
    }

    _setSubmitHint(issues) {
        if (!this.hasSubmitButtonTarget) return
        if (issues.length === 0) {
            this.submitButtonTarget.removeAttribute("title")
        } else {
            this.submitButtonTarget.title = `Before creating: ${issues.join(" · ")}`
        }
    }

    _escape(str) {
        const div = document.createElement("div")
        div.textContent = str
        return div.innerHTML
    }
}
